import { Checkbox, InputNumber, Modal } from 'antd'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import Loader from '@/components/Atoms/Loader/Loader'
import { useTreeTilesStore } from '@/stores/feed/treeTilesStore'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { fetchCategoriesTree } from '@/utils/api/fetch/fetchEntities'
import {
  getApprovalStatusLabel,
  getStatusBadge,
} from '@/utils/catalog/approvalStatus'

import type {
  BoundCharacteristicCategory,
  CategorySelectionNode,
} from '../characteristicCategoryUtils'
import {
  buildCategorySelectionTree,
  collectExpandableIds,
  filterCategorySelectionTree,
  flattenVisibleCategoryTree,
} from '../characteristicCategoryUtils'
import cn from '../DictionaryDetailsModal.module.scss'

const MIN_SLOTS_COUNT = 1
const MAX_SLOTS_COUNT = 10

export type SlotConfigPayloadItem = {
  categoryId: string
  slotsCount: number
}

export type CharacteristicSlotConfigSectionHandle = {
  buildConfigsPayload: () => SlotConfigPayloadItem[]
  markSaved: () => void
}

type CharacteristicSlotConfigSectionProps = {
  characteristicId: string
  boundCategories: BoundCharacteristicCategory[]
  readOnly?: boolean
  onDirtyChange: (dirty: boolean) => void
}

function collectCategoryIds(
  nodes: CategorySelectionNode[],
  boundIds: Set<string>
): { knownIds: Set<string>; eligibleIds: Set<string> } {
  const knownIds = new Set<string>()
  const eligibleIds = new Set<string>()

  const walk = (treeNodes: CategorySelectionNode[]) => {
    treeNodes.forEach((node) => {
      knownIds.add(node.id)
      if (
        boundIds.has(node.id) ||
        node.ancestorIds.some((id) => boundIds.has(id))
      ) {
        eligibleIds.add(node.id)
      }
      walk(node.children)
    })
  }

  walk(nodes)

  return { knownIds, eligibleIds }
}

function renderStatus(status?: 'DRAFT' | 'APPROVED' | 'REJECTED') {
  if (!status || status === 'APPROVED') {
    return <span className={cn.categoryStatusApproved}>Утверждена</span>
  }

  return getStatusBadge(status) ?? getApprovalStatusLabel(status) ?? '—'
}

const CharacteristicSlotConfigSection = forwardRef<
  CharacteristicSlotConfigSectionHandle,
  CharacteristicSlotConfigSectionProps
>(function CharacteristicSlotConfigSection(props, ref) {
  const {
    characteristicId,
    boundCategories,
    readOnly = false,
    onDirtyChange,
  } = props

  const [treeNodes, setTreeNodes] = useState<CategorySelectionNode[]>([])
  const [treeLoading, setTreeLoading] = useState(false)
  const [configsLoading, setConfigsLoading] = useState(false)
  const [configsLoaded, setConfigsLoaded] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [initialConfigs, setInitialConfigs] = useState<Record<string, number>>(
    {}
  )
  const [draftConfigs, setDraftConfigs] = useState<Record<string, number>>({})
  const [searchValue, setSearchValue] = useState('')
  const [expandedIds, setExpandedIds] = useState<string[]>([])
  const [showOnlyChanged, setShowOnlyChanged] = useState(false)
  const [modal, contextHolder] = Modal.useModal()

  const { categoriesTree, setCategoriesTree } = useTreeTilesStore()

  useEffect(() => {
    const loadCategoryData = async () => {
      if (categoriesTree && categoriesTree.length > 0) {
        setTreeNodes(buildCategorySelectionTree(categoriesTree))
        return
      }

      setTreeLoading(true)
      try {
        const tree = await fetchCategoriesTree()
        setCategoriesTree(tree)
        setTreeNodes(buildCategorySelectionTree(tree))
      } catch (error) {
        setLoadFailed(true)
        handleApiError(error, modal)
      } finally {
        setTreeLoading(false)
      }
    }

    loadCategoryData()
  }, [categoriesTree, modal, setCategoriesTree])

  useEffect(() => {
    let cancelled = false

    const loadSlotConfigs = async () => {
      setConfigsLoading(true)
      setConfigsLoaded(false)
      setInitialConfigs({})
      setDraftConfigs({})
      try {
        const response = await api.get(
          `/main/characteristics/${characteristicId}/slot-configs`
        )
        if (cancelled) return

        const configs: Record<string, number> = {}
        ;(response.data.configs ?? []).forEach(
          (config: SlotConfigPayloadItem) => {
            configs[config.categoryId] = config.slotsCount
          }
        )
        setInitialConfigs(configs)
        setDraftConfigs(configs)
        setConfigsLoaded(true)
      } catch (error) {
        if (!cancelled) setLoadFailed(true)
        handleApiError(error, modal)
      } finally {
        if (!cancelled) setConfigsLoading(false)
      }
    }

    loadSlotConfigs()

    return () => {
      cancelled = true
    }
  }, [characteristicId, modal])

  const boundIds = useMemo(
    () => new Set(boundCategories.map((category) => category.id)),
    [boundCategories]
  )
  const { knownIds, eligibleIds } = useMemo(
    () => collectCategoryIds(treeNodes, boundIds),
    [treeNodes, boundIds]
  )

  const isReady =
    configsLoaded && !configsLoading && !treeLoading && treeNodes.length > 0

  const isKeptInPayload = useCallback(
    (categoryId: string) =>
      eligibleIds.has(categoryId) || !knownIds.has(categoryId),
    [eligibleIds, knownIds]
  )
  const filteredTreeNodes = useMemo(
    () => filterCategorySelectionTree(treeNodes, searchValue),
    [treeNodes, searchValue]
  )
  const visibleExpandedIds = useMemo(
    () =>
      searchValue.trim()
        ? collectExpandableIds(filteredTreeNodes)
        : new Set(expandedIds),
    [expandedIds, filteredTreeNodes, searchValue]
  )
  const changedIds = useMemo(() => {
    const ids = new Set([
      ...Object.keys(initialConfigs),
      ...Object.keys(draftConfigs),
    ])
    return new Set(
      [...ids].filter((id) => initialConfigs[id] !== draftConfigs[id])
    )
  }, [initialConfigs, draftConfigs])

  const visibleNodes = useMemo(() => {
    if (!showOnlyChanged)
      return flattenVisibleCategoryTree(filteredTreeNodes, visibleExpandedIds)

    return flattenVisibleCategoryTree(
      filteredTreeNodes,
      collectExpandableIds(filteredTreeNodes)
    ).filter((node) => changedIds.has(node.id))
  }, [changedIds, filteredTreeNodes, showOnlyChanged, visibleExpandedIds])

  const isDirty = useMemo(() => {
    if (!isReady) return false

    // Сравниваем с полным (нефильтрованным) initialConfigs — это то, что сейчас
    // реально хранится на бэке. Если категорию отвязали от характеристики (и она
    // выпала из eligibleIds), её персональный лимит пропадёт из payload — PUT
    // должен уйти, иначе у бэка останется осиротевший лимит на отвязанную категорию.
    const nextEntries = Object.entries(draftConfigs).filter(([id]) =>
      isKeptInPayload(id)
    )
    const initialKeys = Object.keys(initialConfigs)

    if (nextEntries.length !== initialKeys.length) return true

    return nextEntries.some(([id, value]) => initialConfigs[id] !== value)
  }, [isReady, initialConfigs, draftConfigs, isKeptInPayload])

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  useImperativeHandle(
    ref,
    () => ({
      buildConfigsPayload: () =>
        Object.entries(draftConfigs)
          .filter(([id]) => isKeptInPayload(id))
          .map(([categoryId, slotsCount]) => ({ categoryId, slotsCount })),
      markSaved: () => {
        const persistedConfigs = Object.fromEntries(
          Object.entries(draftConfigs).filter(([id]) => isKeptInPayload(id))
        )
        setDraftConfigs(persistedConfigs)
        setInitialConfigs(persistedConfigs)
      },
    }),
    [draftConfigs, isKeptInPayload]
  )

  const handleExpandToggle = (categoryId: string) => {
    setExpandedIds((prevExpandedIds) =>
      prevExpandedIds.includes(categoryId)
        ? prevExpandedIds.filter((id) => id !== categoryId)
        : [...prevExpandedIds, categoryId]
    )
  }

  const handleSlotsCountChange = (
    categoryId: string,
    rawValue: number | null
  ) => {
    setDraftConfigs((prev) => {
      if (rawValue === null) {
        if (!(categoryId in prev)) return prev
        const next = { ...prev }
        delete next[categoryId]
        return next
      }

      const value = Math.max(
        MIN_SLOTS_COUNT,
        Math.min(MAX_SLOTS_COUNT, Math.round(rawValue))
      )
      return { ...prev, [categoryId]: value }
    })
  }

  const handleRevert = (categoryId: string) => {
    setDraftConfigs((prev) => {
      const next = { ...prev }
      if (categoryId in initialConfigs)
        next[categoryId] = initialConfigs[categoryId]
      else delete next[categoryId]
      return next
    })
  }

  const renderPreviousValue = (categoryId: string) => {
    const previous = initialConfigs[categoryId]
    const current = draftConfigs[categoryId]
    if (previous === undefined) return 'Новый лимит'
    if (current === undefined) return `Было ${previous}, лимит удалится`
    return `Было ${previous}`
  }

  const loading = treeLoading || configsLoading

  return (
    <div className={cn.categoryEditor}>
      {contextHolder}
      <div className={cn.selectorSearchBlock}>
        <input
          value={searchValue}
          className={cn.selectorSearch}
          placeholder="Поиск по коду, категории, родителю или пути"
          onChange={(event) => setSearchValue(event.target.value)}
        />
        {isReady && (
          <div className={cn.slotConfigToolbar}>
            <span
              className={
                changedIds.size > 0
                  ? cn.slotConfigChangedCount
                  : cn.slotConfigNoChanges
              }
            >
              {changedIds.size > 0
                ? `Изменено лимитов: ${changedIds.size}`
                : 'Изменений нет'}
            </span>
            <Checkbox
              checked={showOnlyChanged}
              disabled={changedIds.size === 0 && !showOnlyChanged}
              onChange={(event) => setShowOnlyChanged(event.target.checked)}
            >
              Только изменённые
            </Checkbox>
          </div>
        )}
      </div>

      {!loading && loadFailed && (
        <div className={cn.referenceHint}>
          Не удалось загрузить текущие лимиты или дерево категорий —
          редактирование недоступно. Откройте карточку заново.
        </div>
      )}

      {loading ? (
        <div className={cn.selectorLoader}>
          <Loader size={24} />
        </div>
      ) : (
        <div className={[cn.categoryList, cn.slotConfigList].join(' ')}>
          <div
            className={[
              cn.categoryListRow,
              cn.slotConfigListRow,
              cn.categoryListHeader,
            ].join(' ')}
          >
            <div className={cn.categoryCodeCell}>Код</div>
            <div className={cn.categoryNameCell}>Категория</div>
            <div className={cn.categoryParentCell}>Родитель</div>
            <div className={cn.categoryStatusCell}>Статус</div>
            <div className={cn.slotConfigSlotsCell}>Слотов</div>
          </div>
          {visibleNodes.length > 0 ? (
            <div className={cn.categoryListBody}>
              {visibleNodes.map((node) => {
                const isExpanded = visibleExpandedIds.has(node.id)
                const hasChildren = node.children.length > 0
                const isEligible = eligibleIds.has(node.id)
                const value = draftConfigs[node.id]
                const isChanged = changedIds.has(node.id)

                return (
                  <div
                    key={node.id}
                    className={[
                      cn.categoryListRow,
                      cn.slotConfigListRow,
                      cn.categoryStaticRow,
                      isChanged ? cn.slotConfigRowChanged : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <div className={cn.categoryCodeCell}>{node.code}</div>
                    <div className={cn.categoryNameCell}>
                      <div
                        className={cn.categoryTreeLabel}
                        style={{ '--tree-depth': node.depth }}
                      >
                        {hasChildren ? (
                          <button
                            type="button"
                            className={[
                              cn.categoryExpandButton,
                              isExpanded ? cn.categoryExpandButtonOpen : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={() => handleExpandToggle(node.id)}
                          >
                            <span className={cn.categoryExpandIcon}>{'>'}</span>
                          </button>
                        ) : (
                          <span className={cn.categoryExpandSpacer} />
                        )}
                        <div className={cn.categoryRowMain}>
                          <span className={cn.categoryRowTitle}>
                            {node.description}
                          </span>
                          {node.depth > 0 && (
                            <span className={cn.categoryRowPath}>
                              {node.path}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={cn.categoryParentCell}>
                      {node.parentDescription || '—'}
                    </div>
                    <div className={cn.categoryStatusCell}>
                      {renderStatus(node.status)}
                    </div>
                    <div className={cn.slotConfigSlotsCell}>
                      {isEligible ? (
                        <InputNumber
                          size="small"
                          precision={0}
                          className={cn.slotConfigSlotsInput}
                          min={MIN_SLOTS_COUNT}
                          max={MAX_SLOTS_COUNT}
                          value={value}
                          placeholder="Не заполнено"
                          disabled={readOnly || !isReady}
                          onChange={(v) => handleSlotsCountChange(node.id, v)}
                        />
                      ) : (
                        '—'
                      )}
                      {isChanged && (
                        <div className={cn.slotConfigPreviousValue}>
                          <span>{renderPreviousValue(node.id)}</span>
                          {!readOnly && (
                            <button
                              type="button"
                              className={cn.slotConfigRevertButton}
                              onClick={() => handleRevert(node.id)}
                            >
                              вернуть
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className={cn.categoryListEmpty}>Категории не найдены</div>
          )}
        </div>
      )}
    </div>
  )
})

export default CharacteristicSlotConfigSection
